import { ref, onUnmounted, onMounted, inject, readonly, type Ref } from 'vue';
import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ConfigService } from "@/contracts/ConfigService";
import { MessageService, DecodedEnvelope, Unsubscribe } from "@/contracts/MessageService";
import { MessageEnvelope, MessageProperties } from "@/domain/messaging/MessageEnvelope";
import { TelemetryContract } from '@/contracts/TelemetryContract';
import type { Tell } from '@/services/Tell';

interface UseClientMessagingOptions {
	/** The name of the SignalR hub to connect to */
	hubName: string;
	/** Whether to automatically connect when the component mounts. Default: true */
	autoConnect?: boolean;
	/** Optional message filter function to validate if a message should be processed */
	messageFilter?: (envelope: DecodedEnvelope<unknown>, methodName: string) => boolean;
	/** Number of consecutive SignalR drops before notifying the user via Tell. Default: 2 */
	signalRFailureThreshold?: number;
}

interface UseClientMessagingReturn {
	isConnected: Readonly<Ref<boolean>>;
	isConnecting: Readonly<Ref<boolean>>;
	connect: () => Promise<void>;
	disconnect: () => Promise<void>;
	onMessage: <TBody>(
		methodName: string,
		callback: (envelope: DecodedEnvelope<TBody>) => void,
		messageProperties?: MessageProperties
	) => Unsubscribe;
	offMessage: (methodName: string, callback?: () => void) => void;
	sendMessage: <T>(methodName: string, data: T, messageProperties?: MessageProperties) => Promise<void>;
}

/**
 * Vue composable for SignalR client messaging
 * @param options Configuration options for the composable
 * @returns Object containing connection state and messaging methods
 */
export function useClientMessaging(
	options: UseClientMessagingOptions
): UseClientMessagingReturn {
	const configService = inject<ConfigService>('configService');
	const telemetry = inject<TelemetryContract>('telemetry');
	const tell = inject<Tell>('tell');

	// Destructure options with defaults
	const { hubName, autoConnect = true, messageFilter, signalRFailureThreshold = 2 } = options;

	// Tracks consecutive drops to avoid false-positive notifications on brief blips
	let consecutiveFailureCount = 0;

	if (!configService) {
		throw new Error('ConfigService must be provided via Vue injection');
	}

	if (!telemetry) {
		throw new Error('TelemetryContract must be provided via Vue injection');
	}

	// Reactive state
	const isConnected = ref(false);
	const isConnecting = ref(false);
	const connection = ref<HubConnection | null>(null);

	/**
	 * We keep a map of method -> wrapper so we can (re)attach or remove precisely.
	 * Wrapper signature must match how the hub invokes client methods.
	 */
	const handlers = new Map<string, (raw: Uint8Array | string | object, props: MessageProperties) => void>();

	// Start the SignalR hub connection
	const connect = async (): Promise<void> => {
		if (connection.value || isConnecting.value) return;
		isConnecting.value = true;

		try {
			const connectionResult = await configService.getMessageClientConnection(hubName);
			if (!connectionResult.success) {
				const exceptionTelemetry: IExceptionTelemetry = {
					exception: new Error("Error while negotiating for SignalR config"),
					severityLevel: SeverityLevel.Error,
					properties: { connectionResult, hubName }
				};
				telemetry.trackException(exceptionTelemetry);
				
				// Retry after delay
				setTimeout(() => {
					isConnecting.value = false;
					connect();
				}, 5000);
				return;
			}

			connection.value = new HubConnectionBuilder()
				.withUrl(connectionResult.connectionInfo.url, {
					accessTokenFactory: () => connectionResult.connectionInfo.accessToken
				})
				.configureLogging(LogLevel.Debug)
				.build();

			const attachAllHandlers = () => {
				for (const [method, handler] of handlers) {
					connection.value!.off(method);        // idempotent: clear any stale refs
					connection.value!.on(method, handler);
				}
			};

			connection.value.onreconnected(() => {
				consecutiveFailureCount = 0;
				isConnected.value = true;
				attachAllHandlers();
			});

			connection.value.onclose(() => {
				if (isConnected.value) {
					consecutiveFailureCount++;
					if (consecutiveFailureCount >= signalRFailureThreshold) {
						tell?.warning(
							'The real-time connection was lost. ' +
							'Please refresh the page to restore live updates.'
						);
					}
				}
				isConnected.value = false;
				connection.value = null;
				
				// Retry connection after delay
				setTimeout(() => {
					isConnecting.value = false;
					connect();
				}, 5000);
			});

			await connection.value.start();
			attachAllHandlers();
			consecutiveFailureCount = 0;
			isConnected.value = true;
		} catch (err) {
			const exceptionTelemetry: IExceptionTelemetry = {
				exception: new Error("Error while starting SignalR connection"),
				severityLevel: SeverityLevel.Error,
				properties: { hubName, err: String(err) }
			};
			telemetry.trackException(exceptionTelemetry);
			
			// Retry after delay
			setTimeout(() => {
				isConnecting.value = false;
				connect();
			}, 5000);
		} finally {
			isConnecting.value = false;
		}
	};

	/**
	 * Subscribe to messages from the SignalR hub
	 * @param methodName The hub method name to listen for
	 * @param callback Function to call when message is received
	 * @param messageProperties Optional message properties
	 * @returns Unsubscribe function
	 */
	const onMessage = <TBody>(
		methodName: string,
		callback: (envelope: DecodedEnvelope<TBody>) => void,
		messageProperties?: MessageProperties
	): Unsubscribe => {
		const wrapper = (raw: Uint8Array | string | object, msgProps: MessageProperties) => {
			try {
				// fromSignalR always yields an envelope with Uint8Array payload
				const envelope = MessageEnvelope.fromSignalR(methodName, raw, msgProps);

				// Decode the JSON body to requested type
				const decoded = envelope.getDecodedPayload<TBody>();

				// Expose decoded on the same instance (keeps prototype methods intact)
				const typed = Object.assign(envelope, { decoded }) as DecodedEnvelope<TBody>;

				// Apply message filter if provided
				if (messageFilter) {
					const shouldProcess = messageFilter(typed, methodName);
					
					if (!shouldProcess) {
						// Message filtered out - track and return without calling callback
						telemetry?.trackTrace({
							message: `Message filtered out for ${methodName} on ${hubName}`,
							severityLevel: 1, // Information
						});
						return;
					}
				}

				callback(typed);
			} catch (error) {
				telemetry.trackException({
					exception: error instanceof Error ? error : new Error("MessageEnvelope deserialization failed"),
					properties: { source: "useClientMessaging.onMessage", methodName, hubName }
				});
			}
		};

		// cache for (re)attach
		handlers.set(methodName, wrapper);

		// attach now if connected
		if (connection.value) {
			connection.value.on(methodName, wrapper);
		}

		// return precise unsubscriber
		return () => {
			const current = handlers.get(methodName);
			if (current) {
				connection.value?.off(methodName, current);
				handlers.delete(methodName);
			}
		};
	};

	/**
	 * Remove a message listener
	 * @param methodName The hub method name to stop listening for
	 * @param callback Optional callback (for compatibility)
	 */
	const offMessage = (methodName: string, callback?: () => void): void => {
		const wrapper = handlers.get(methodName);
		if (wrapper) {
			connection.value?.off(methodName, wrapper);
			handlers.delete(methodName);
		}
	};

	/**
	 * Send a message to the SignalR hub
	 * @param methodName The hub method name to invoke
	 * @param data The data to send
	 * @param messageProperties Optional message properties
	 */
	const sendMessage = async <T>(
		methodName: string, 
		data: T, 
		messageProperties?: MessageProperties
	): Promise<void> => {
		if (!connection.value) {
			console.warn(`Cannot send message to ${methodName}: SignalR not connected to ${hubName}`);
			return;
		}

		try {
			await connection.value.invoke(methodName, data);
			console.log(`Message sent to ${hubName}.${methodName}:`, data);
		} catch (err) {
			console.error(`Error while sending message to ${hubName}.${methodName}:`, err);
			telemetry.trackException({
				exception: err instanceof Error ? err : new Error("SignalR sendMessage failed"),
				properties: { source: "useClientMessaging.sendMessage", methodName, hubName }
			});
		}
	};

	/**
	 * Stop the SignalR connection
	 */
	const disconnect = async (): Promise<void> => {
		if (!connection.value) return;

		try {
			await connection.value.stop();
			console.log(`SignalR Disconnected from ${hubName}.`);
		} catch (error) {
			console.error(`Error disconnecting from ${hubName}:`, error);
		} finally {
			connection.value = null;
			isConnected.value = false;
			handlers.clear();
		}
	};

	// Auto-connect on mount if enabled
	if (autoConnect) {
		onMounted(() => {
			connect();
		});
	}

	// Cleanup on component unmount
	onUnmounted(() => {
		disconnect();
	});

	return {
		isConnected: readonly(isConnected),
		isConnecting: readonly(isConnecting),
		connect,
		disconnect,
		onMessage,
		offMessage,
		sendMessage
	};
}
